import React, { useState } from 'react';
import SceneEditor from './SceneEditor';
import ConfirmDialog from './ConfirmDialog';
import SVGColorBlock from './SVGColorBlock';
import { useCurrentSystem } from './context/SystemContext';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from './common/firebase';
import { Edit, Trash2, Play, Lock, MoreVertical, Check, Repeat } from 'lucide-react';
import { getScenePreviewColors, getSceneType } from './sceneUtils';

function SceneCard({ scene, active, activateScene, deleteScene, guest }) {
	const { currentSystem } = useCurrentSystem();
	const [editing, setEditing] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);

	const editScene = () => setEditing(true);
	const doActivate = () => activateScene(scene);
	const doDelete = () => setDeleteConfirm(true);

	const deleteYes = async () => {
		await deleteScene(scene);
		setDeleteConfirm(false);
	};

	const lockScene = async () => {
		const locked = !scene.locked;
		await updateDoc(doc(firestore, 'scenes', scene.uid), { locked });
		setMenuOpen(false);
	};

	return (
		<div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-all group">
			<div className="p-3 flex justify-between items-center bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
				<div className="font-medium text-sm truncate pr-2 flex items-center gap-1.5">
					{scene?.name || 'Blackout'}
					{scene && getSceneType(scene) === 'cycle' && (
						<Repeat size={12} className="text-[#880e4f] flex-shrink-0" title="Cycle" />
					)}
				</div>
				<div className="flex gap-1 items-center">
					{active && scene?.locked && <Lock size={14} className="text-gray-500" />}
					{active && !scene?.locked && <Check size={14} className="text-emerald-500" />}
				</div>
			</div>

			<div onClick={doActivate} className="cursor-pointer p-3 flex justify-center bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
				<SVGColorBlock size={96} data={scene ? getScenePreviewColors(scene) : [{ r: 0, g: 0, b: 0 }]} fill="#3b82f6" />
			</div>

			<div className="flex border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1.5 gap-1 justify-around">
				{scene && (
					<button 
						disabled={scene.locked} 
						onClick={(e) => { e.stopPropagation(); editScene(); }} 
						className="p-1.5 text-gray-500 hover:text-[#880e4f] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md disabled:opacity-50 transition-colors"
						title="Edit"
					>
						<Edit size={16} />
					</button>
				)}
				<button 
					onClick={(e) => { e.stopPropagation(); doActivate(); }} 
					className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
					title="Activate"
				>
					<Play size={16} />
				</button>
				{scene && (
					<button 
						onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }} 
						className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
						title="More"
					>
						<MoreVertical size={16} />
					</button>
				)}
			</div>

			{menuOpen && scene && (
				<div className="border-t border-gray-200 dark:border-gray-700 text-xs bg-white dark:bg-gray-900">
					<div 
						onClick={(e) => { e.stopPropagation(); lockScene(); }} 
						className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer flex items-center gap-2 text-gray-600 dark:text-gray-400"
					>
						<Lock size={14} /> {scene.locked ? "Unlock Scene" : "Lock Scene"}
					</div>
					{deleteScene && (
						<div 
							onClick={(e) => { e.stopPropagation(); doDelete(); }} 
							className="px-3 py-2 hover:bg-red-50 dark:hover:bg-red-950 text-red-600 cursor-pointer flex items-center gap-2 border-t border-gray-100 dark:border-gray-800"
						>
							<Trash2 size={14} /> Delete Scene
						</div>
					)}
				</div>
			)}

			{editing && <SceneEditor open={editing} scene={scene} doneEdit={() => setEditing(false)} numColors={currentSystem?.num_colors} />}
			{deleteConfirm && deleteScene && (
				<ConfirmDialog 
					open={deleteConfirm} 
					message={`Delete scene "${scene.name}"? This cannot be undone.`} 
					title="Delete Scene?" 
					yes_text="Delete" 
					no_text="Cancel" 
					on_yes={deleteYes} 
					on_no={() => setDeleteConfirm(false)} 
				/>
			)}
		</div>
	);
}

export default SceneCard;